import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-8516">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-8517">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-8518">
              <span className="text-xs font-bold text-indigo-800" data-id="element-8519">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-8520">
            <div className="text-center" data-id="element-8521">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-8522">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-8523">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-8524">→</div>
            <div className="text-center" data-id="element-8525">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-8526">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-8527">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-8528">
            <table className="w-full text-sm" data-id="element-8529">
              <thead data-id="element-8530">
                <tr className="bg-indigo-100/70" data-id="element-8531">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-8532">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-8533">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-8534">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-8535">
                      <td className="px-3 py-1.5" data-id="element-8536">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-8537">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-8538">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-8539">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-8540">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-8541">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-8542">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-8543">
            <span className="text-2xl" data-id="element-8544">📝</span>
            <div className="text-left" data-id="element-8545">
              <div className="text-sm font-bold text-indigo-900" data-id="element-8546">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-8547">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-8548">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-8549">
            <div className="text-center" data-id="element-8550">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-8551">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-8552">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-8553">→</div>
            <div className="text-center" data-id="element-8554">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-8555">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-8556">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-8557">
          <table className="w-full" data-id="element-8558">
            <thead data-id="element-8559">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-8560">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-8561">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-8562">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-8563">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-8564">
                    <td className="px-4 py-3" data-id="element-8565">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-8566">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-8567">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-8568">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-8569">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-8570">
            <div className="flex items-start gap-3" data-id="element-8571">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-8572">
                <span className="text-blue-600 text-lg" data-id="element-8573">🔍</span>
              </div>
              <div data-id="element-8574">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-8575">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-8576">
                  De <strong data-id="element-8577">stam</strong> krijg je door <strong data-id="element-8578">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-8579">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-8580">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-8581">
              <div className="flex items-start gap-3" data-id="element-8582">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-8583">
                  <span className="text-emerald-600 text-lg" data-id="element-8584">📋</span>
                </div>
                <div data-id="element-8585">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-8586">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-8587">
                    <p data-id="element-8588">
                      <strong data-id="element-8589">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8590">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-8591">
                      <strong data-id="element-8592">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8593">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-8594">
                      <strong data-id="element-8595">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8596">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-8597">
                      <strong data-id="element-8598">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-8599">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-8600">
            <div className="flex items-start gap-3" data-id="element-8601">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-8602">
                <span className="text-amber-600 text-lg" data-id="element-8603">💡</span>
              </div>
              <div data-id="element-8604">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-8605">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-8606">
                  Bij <strong data-id="element-8607">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-8608">jij loopt</em> wordt{' '}
                  <em data-id="element-8609">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}